import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.min.css";
import "./SummaryGrid.css";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import OAuthService from "src/app/auth/services/oAuthService";

const processData = (data) => {
  const flattenedData = [];
  const flattenRowRecursive = (row, parentPath) => {
    row["invoiceAmount"] = row.invoiceAmount.toLocaleString();
    row["paid"] = row.paid.toLocaleString();
    row["balance"] = row.balance.toLocaleString();
    row["zeroToThirty"] =
      row.zeroToThirty !== null ? row.zeroToThirty.toLocaleString() : null;
    row["thirtyOneToSixty"] =
      row.thirtyOneToSixty !== null
        ? row.thirtyOneToSixty.toLocaleString()
        : null;
    row["sixtyOneToNinty"] =
      row.sixtyOneToNinty !== null
        ? row.sixtyOneToNinty.toLocaleString()
        : null;
    row["nintyOneToOneTwenty"] =
      row.nintyOneToOneTwenty !== null
        ? row.nintyOneToOneTwenty.toLocaleString()
        : null;
    row["oneTwentyOneToOneFifty"] =
      row.oneTwentyOneToOneFifty !== null
        ? row.oneTwentyOneToOneFifty.toLocaleString()
        : null;
    row["oneFiftyOneToOneEighty"] =
      row.oneFiftyOneToOneEighty !== null
        ? row.oneFiftyOneToOneEighty.toLocaleString()
        : null;
    row["oneEightyOneToTwoHundredTen"] =
      row.oneEightyOneToTwoHundredTen !== null
        ? row.oneEightyOneToTwoHundredTen.toLocaleString()
        : null;
    row["twoHundredElevenToTwoFourty"] =
      row.twoHundredElevenToTwoFourty !== null
        ? row.twoHundredElevenToTwoFourty.toLocaleString()
        : null;
    row["twoHundredFourtyOneToTwoHundredSeventy"] =
      row.twoHundredFourtyOneToTwoHundredSeventy !== null
        ? row.twoHundredFourtyOneToTwoHundredSeventy.toLocaleString()
        : null;
    row["twoHundredSenventyOneToThreeHundred"] =
      row.twoHundredSenventyOneToThreeHundred !== null
        ? row.twoHundredSenventyOneToThreeHundred.toLocaleString()
        : null;
    row["threeHundredOneToThreeThirty"] =
      row.threeHundredOneToThreeThirty !== null
        ? row.threeHundredOneToThreeThirty.toLocaleString()
        : null;
    row["threeHundredThirtyOneToThreeHundredSixty"] =
      row.threeHundredThirtyOneToThreeHundredSixty !== null
        ? row.threeHundredThirtyOneToThreeHundredSixty.toLocaleString()
        : null;
    row["moreThenOneYear"] =
      row.moreThenOneYear !== null
        ? row.moreThenOneYear.toLocaleString()
        : null;
    const dataPath = [...parentPath, row.orderCategory];
    flattenedData.push({ ...row, dataPath });
    if (row.subCategories) {
      row.subCategories.forEach((underling) =>
        flattenRowRecursive(underling, dataPath)
      );
    }
  };
  data.forEach((row) => flattenRowRecursive(row, []));
  return flattenedData;
};

const SummaryGrid = (props) => {
  const [rowData, setRowData] = useState();
  const [columnDefs, setColumnDefs] = useState([
    { field: "customerName", headerName: "Customer Name" },
    { field: "customerCode", headerName: "Customer Code" },
    { field: "city", headerName: "City/Country" },
    { field: "physical", headerName: "Physical" },
    { field: "online", headerName: "Online" },
    { field: "total", headerName: "Total" },
    { field: "status", headerName: "Status" },
  ]);

  const stateRef = useRef();
  const [stateData, setStateData] = useState({
    filter: "",
    columnState: "",
    columnGroupState: "",
  });
  const gridStateId = useRef();
  const saveGridStateName = "TrainingSummary";
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 50,
    };
  }, []);
  //   const autoGroupColumnDef = useMemo(() => {
  //     return {
  //       headerName: 'Order Type',
  //       field: 'orderCategory',
  //       cellRendererParams: {
  //         suppressCount: true,
  //       },
  //       filter: 'agSetColumnFilter',
  //       filterParams: {
  //         treeList: true,
  //       },
  //       minWidth: 280,
  //     };
  //   }, []);
  const getDataPath = useMemo(() => {
    return (data) => data.dataPath;
  }, []);
  const getRowId = useMemo(() => {
    return (params) => params.data._id;
  }, []);

  const autoSizeAll = useCallback(
    (value) => {
      const allColumnIds = [];
      props.gridRef.current.columnApi.getColumns().forEach((column) => {
        allColumnIds.push(column?.getId());
      });
      props.gridRef.current.columnApi.autoSizeColumns(allColumnIds, value);
    },
    [rowData]
  );

  const onGridReady = useCallback((params) => {
    // setRowData(processData(props.rowData));
    autoSizeAll(false);
  }, []);

  const onFilterChanged = (params) => {
    stateData.filter = params?.api?.getFilterModel();
    setStateData(stateData);
  };

  const onColumnStateChanged = (params) => {
    stateData.columnState = params?.columnApi?.getColumnState();
    stateData.columnGroupState = params?.columnApi?.getColumnGroupState();
  };
  const onColumnGroupChange = (params) => {
    onColumnStateChanged(params);
  };

  const onFirstDataLoad = async (params) => {
    const url = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.gridState}`;
    const savedState = await OAuthService.getList(
      url + "/fetchState?screenName=" + saveGridStateName
    );
    const data = savedState?.data;

    if (data) {
      gridStateId.current = data?.id;
      data.screenState.filter &&
        params.api.setFilterModel(data?.screenState.filter);
      data.screenState.columnState &&
        params.columnApi.applyColumnState({
          state: data?.screenState?.columnState,
          applyOrder: true,
        });
      data.screenState.columnGroupState &&
        params.columnApi.setColumnGroupState(
          data?.screenState?.columnGroupState
        );
    }
  };

  return (
    <>
      <div
        className="ag-theme-alpine-dark dark-custom-alpine"
        style={{ height: 350, width: "100%" }}
      >
        <AgGridReact
          ref={props.gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          //   autoGroupColumnDef={autoGroupColumnDef}
          treeData={true}
          animateRows={true}
          //   groupDefaultExpanded={-1}
          getDataPath={getDataPath}
          getRowId={getRowId}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataLoad}
          onFilterChanged={onFilterChanged}
          onSortChanged={onColumnStateChanged}
          onColumnVisible={onColumnStateChanged}
          onColumnPinned={onColumnStateChanged}
          onColumnResized={onColumnStateChanged}
          onColumnMoved={onColumnStateChanged}
          onColumnRowGroupChanged={onColumnGroupChange}
        ></AgGridReact>
      </div>
    </>
  );
};

export default SummaryGrid;
